/* iter-49 — Spell-check issues drawer.
 *
 * Renders the list of Claude-flagged issues returned by
 * POST /api/projects/{pid}/spellcheck. Each item shows:
 *   - kind badge (spelling / grammar / punctuation / awkward)
 *   - the offending span
 *   - the suggested fix
 *   - "Apply" → replaces the FIRST occurrence of `span` in `chapter.content`
 *     with `suggestion` and saves via chaptersApi.update.
 *
 * "Apply all" runs through every issue in order. We intentionally do not
 * use regex matching — span is treated as a literal string — so an author
 * can review-and-skip without worrying about side effects.
 *
 * Feb-2026 — Added a collapsible "Enhance with extensions" section that
 * shows step-by-step install instructions for Grammarly and ProWritingAid
 * browser extensions (no API key required).
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Check, Loader2, SpellCheck, ChevronDown, ChevronUp, ExternalLink, Chrome, Puzzle } from 'lucide-react';
import { toast } from 'sonner';
import { chaptersApi } from '@/lib/api';

const KIND_TONE = {
  spelling:    { dot: 'bg-rose-500',   label: 'Spelling'    },
  grammar:     { dot: 'bg-amber-500',  label: 'Grammar'     },
  punctuation: { dot: 'bg-sky-500',    label: 'Punctuation' },
  awkward:     { dot: 'bg-violet-500', label: 'Awkward'     },
};

const EXTENSION_STEPS = [
  {
    name: 'Grammarly',
    color: 'text-emerald-600',
    dotColor: 'bg-emerald-500',
    steps: [
      { label: 'Install the extension', href: 'https://www.grammarly.com/browser/chrome', linkText: 'Get Grammarly for Chrome' },
      { label: 'Create a free Grammarly account (or sign in).' },
      { label: 'Return to this editor — Grammarly activates automatically on the prose paragraphs.' },
      { label: 'Click the Grammarly icon in your browser bar to see inline suggestions.' },
    ],
  },
  {
    name: 'ProWritingAid',
    color: 'text-violet-600',
    dotColor: 'bg-violet-500',
    steps: [
      { label: 'Install the extension', href: 'https://prowritingaid.com/en/App/GetStartedBrowser', linkText: 'Get ProWritingAid for Chrome' },
      { label: 'Create a free ProWritingAid account (or sign in).' },
      { label: 'Return to this editor — the extension activates automatically.' },
      { label: 'Highlight any text or click the toolbar icon to run a full style report.' },
    ],
  },
];

function ExtensionInstallHelp() {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-panel-border bg-muted/30">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex w-full items-center justify-between px-3 py-2 text-[11.5px] font-medium text-muted-foreground hover:text-foreground"
        data-testid="spellcheck-extension-help-toggle"
      >
        <span className="flex items-center gap-1.5">
          <Puzzle className="h-3.5 w-3.5 text-sky-500" />
          Enhance with Grammarly &amp; ProWritingAid extensions
        </span>
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="space-y-4 px-3 pb-3" data-testid="spellcheck-extension-help-panel">
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Both tools work via <strong>free browser extensions</strong> — no API key needed.
            Once installed, they auto-activate on every paragraph in this editor.
          </p>
          {EXTENSION_STEPS.map(tool => (
            <div key={tool.name} className="space-y-1.5">
              <div className={`flex items-center gap-1.5 text-[11.5px] font-semibold ${tool.color}`}>
                <span className={`h-2 w-2 rounded-full ${tool.dotColor}`} />
                {tool.name}
              </div>
              <ol className="space-y-1 pl-3">
                {tool.steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                    <span className="shrink-0 rounded bg-muted px-1 py-0.5 font-mono text-[9px] h-fit mt-0.5">{i + 1}</span>
                    <span>
                      {step.href
                        ? <><a href={step.href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-primary hover:underline">{step.linkText} <ExternalLink className="h-2.5 w-2.5" /></a>{step.label.replace(step.linkText || '', '')}</>
                        : step.label}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
          <p className="text-[10.5px] text-muted-foreground italic border-t border-panel-border pt-2">
            Both extensions work on Chrome, Edge and Firefox. Safari support varies.
          </p>
        </div>
      )}
    </div>
  );
}

export function SpellCheckDrawer({ open, onClose, issues = [], chapter, onChapterChanged }) {
  const [applyingIdx, setApplyingIdx] = useState(null);
  const [applyingAll, setApplyingAll] = useState(false);
  const [skipped, setSkipped] = useState(() => new Set());

  if (!open) return null;

  const applyOne = async (issue, idx) => {
    if (!chapter) return;
    const current = chapter.content || '';
    if (!current.includes(issue.span)) {
      toast.error('Span no longer in text — skipped.');
      setSkipped((s) => new Set(s).add(idx));
      return;
    }
    setApplyingIdx(idx);
    try {
      const next = current.replace(issue.span, issue.suggestion);
      await chaptersApi.update(chapter.id, { content: next });
      toast.success('Fix applied.');
      await onChapterChanged?.();
      setSkipped((s) => new Set(s).add(idx));
    } catch {
      toast.error('Could not apply fix.');
    } finally {
      setApplyingIdx(null);
    }
  };

  const applyAll = async () => {
    if (!chapter) return;
    setApplyingAll(true);
    let content = chapter.content || '';
    let applied = 0;
    const newSkipped = new Set(skipped);
    issues.forEach((iss, idx) => {
      if (newSkipped.has(idx)) return;
      if (content.includes(iss.span)) {
        content = content.replace(iss.span, iss.suggestion);
        applied += 1;
        newSkipped.add(idx);
      }
    });
    // iter55f97 — After the batch replace, ANY remaining issue whose
    // span is no longer present in the final content (e.g. because an
    // earlier fix overlapped it, or the replacement swept it out) must
    // be removed from the pending list too — otherwise the writer sees
    // ghost "Apply" buttons that would just error with "span no longer
    // in text" on click.
    let stripped = 0;
    issues.forEach((iss, idx) => {
      if (newSkipped.has(idx)) return;
      if (!content.includes(iss.span)) {
        newSkipped.add(idx);
        stripped += 1;
      }
    });
    if (applied === 0 && stripped === 0) {
      toast.info('No applicable issues.');
      setApplyingAll(false);
      return;
    }
    try {
      if (applied > 0) {
        await chaptersApi.update(chapter.id, { content });
      }
      const parts = [];
      if (applied  > 0) parts.push(`Applied ${applied} fix${applied === 1 ? '' : 'es'}`);
      if (stripped > 0) parts.push(`removed ${stripped} stale option${stripped === 1 ? '' : 's'}`);
      toast.success(parts.join(' · '));
      setSkipped(newSkipped);
      await onChapterChanged?.();
    } catch {
      toast.error('Could not save changes.');
    } finally {
      setApplyingAll(false);
    }
  };

  const visibleCount = issues.filter((_, idx) => !skipped.has(idx)).length;

  return (
    <div
      data-testid="spellcheck-drawer"
      className="absolute right-0 top-0 z-30 flex h-full w-[360px] flex-col border-l border-panel-border bg-card shadow-lg"
    >
      <div className="flex items-center justify-between border-b border-panel-border px-3 py-2">
        <div className="flex items-center gap-2">
          <SpellCheck className="h-3.5 w-3.5 text-primary" />
          <span className="text-[14px] font-semibold">Spell-check</span>
          <Badge variant="outline" className="h-5 px-1.5 text-[13px]">
            {visibleCount} pending
          </Badge>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0" data-testid="spellcheck-close">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Extension install help — always visible, collapsible */}
      <ExtensionInstallHelp />

      {issues.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6 text-center text-[13.5px] text-muted-foreground">
          The passage is clean — no issues found.
        </div>
      ) : (
        <>
          <div className="border-b border-panel-border px-3 py-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={applyAll}
              disabled={applyingAll || visibleCount === 0}
              data-testid="spellcheck-apply-all"
              className="h-7 w-full gap-1.5 text-[13.5px]"
            >
              {applyingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Apply all {visibleCount}
            </Button>
          </div>
          <div className="flex-1 space-y-2 overflow-y-auto p-3 scroll-thin">
            {issues.map((iss, idx) => {
              const tone = KIND_TONE[iss.kind] || KIND_TONE.grammar;
              const isSkipped = skipped.has(idx);
              return (
                <div
                  key={`iss-${idx}`}
                  data-testid={`spellcheck-issue-${idx}`}
                  className={`rounded-md border border-panel-border bg-background p-2 text-[13.5px] transition ${isSkipped ? 'opacity-40' : ''}`}
                >
                  <div className="mb-1 flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
                    <span className="text-[9.5px] uppercase tracking-wider text-muted-foreground">{tone.label}</span>
                  </div>
                  <div className="mb-1 line-through text-muted-foreground">{iss.span}</div>
                  <div className="text-foreground">{iss.suggestion}</div>
                  {iss.note && <div className="mt-1 text-[13px] italic text-muted-foreground">{iss.note}</div>}
                  {!isSkipped && (
                    <div className="mt-2 flex gap-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => applyOne(iss, idx)}
                        disabled={applyingIdx === idx}
                        data-testid={`spellcheck-apply-${idx}`}
                        className="h-6 gap-1 px-2 text-[13px]"
                      >
                        {applyingIdx === idx ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                        Apply
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSkipped((s) => new Set(s).add(idx))}
                        data-testid={`spellcheck-skip-${idx}`}
                        className="h-6 gap-1 px-2 text-[13px] text-muted-foreground"
                      >
                        Skip
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
